import type { LogsEvent } from '@datadog/browser-logs';
import type { RumEvent } from '@datadog/browser-rum';

// Network failures that are noise, not defects. Both SDKs forward every failed
// request by default — RUM as an error event, Logs via forwardErrorsToLogs — so
// both inits need the same filter, or the noise just moves from one explorer to
// the other (it lived in Logs for four months after the RUM side alone was
// filtered).
//
// - The updater polls /assets/VERSION every 5 minutes for as long as a tab
//   stays open. A laptop waking up or Wi-Fi roaming fails the poll with a
//   status-0 fetch error the poll itself already tolerates, and a single tab
//   left open overnight produces a dozen of them.
// - Plausible is fire-and-forget analytics loaded from index.html. Its event
//   POSTs fail in the same offline windows, and some browsers block the host
//   outright; either way nothing in the app depends on the request.
const VERSION_POLL_PATH = '/assets/VERSION';
const PLAUSIBLE_ORIGIN = 'https://plausible.io/';

const isIgnoredNetworkUrl = (url: string | undefined): boolean =>
  url !== undefined && (url.endsWith(VERSION_POLL_PATH) || url.startsWith(PLAUSIBLE_ORIGIN));

/** RUM `beforeSend`: keep everything except a failed request to an ignored URL. */
export const keepRumEvent = (event: RumEvent): boolean =>
  !(
    event.type === 'error' &&
    event.error.source === 'network' &&
    isIgnoredNetworkUrl(event.error.resource?.url)
  );

/** Logs `beforeSend`: keep everything except a failed request to an ignored URL. */
export const keepLogsEvent = (event: LogsEvent): boolean =>
  !(event.origin === 'network' && isIgnoredNetworkUrl(event.http?.url));
